import React, { useState } from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { ENTER_NAME } from 'Pages/GMS/ISR/ISRConstants';

type CreateListDialogProps = {
    id: string;
    dialogHeader: string;
    labelName: string;
    value?: string;
    actionLabel?: string;
    onClose: () => void;
    onSubmit: (name: string) => void;
    showIndicator?: boolean;
    message?: string;
    multiCreate?: boolean;
    entityName?: string;
}

/**
 * The create-entity name prompt, on DS Dialog + TextField + Buttons (§ 85).
 * The legacy overlay's input carried the literal STRING
 * `className={\`tw-w-auto…\`}` as its class attribute — an unparsed template
 * pasted into JSX, so the field had been rendering unstyled since it shipped.
 * Escape and scrim-click now close the dialog through the same `onClose` the
 * X and Cancel use. Submit/clear/multiCreate behavior is unchanged.
 *
 * `showIndicator` is the parent's in-flight flag (ListView passes
 * `createListInProgress`), and since § 209 it also GATES the dialog: the
 * create buttons wear the DS loading state and every close route is shut
 * (Cancel disabled, X/Escape no-op) while the parent's `saveList` runs — the
 * call is not abortable, and `handleSave` has no re-entry guard, so a
 * mid-flight click used to fire a second create and a mid-flight close only
 * hid the dialog while the toast + navigation still landed. The parent's
 * `finally` always drops the flag, so this can never wedge shut.
 */
const CreateListDialog = ({
    id, dialogHeader = "", labelName = "", value,
    onClose, onSubmit,
    actionLabel, showIndicator = false, message,
    multiCreate = false, entityName = 'Item'
}: CreateListDialogProps) => {
    const [name, setName] = useState(value ?? '');
    const [justCreated, setJustCreated] = useState(false);

    const handleCreateAndAddMore = () => {
        onSubmit(name);
        setName("");
        setJustCreated(true);
        setTimeout(() => setJustCreated(false), 2000);
    };

    const handleCreate = () => {
        onSubmit(name);
        onClose();
    };

    const handleSingleCreate = () => {
        onSubmit(name);
        setName("");
    };

    /* One guard covers every close route — Cancel, the X, Escape (Dialog
       routes all three through `onClose`). See the docblock. */
    const guardedClose = () => {
        if (showIndicator) return;
        onClose();
    };

    const footer = (
        <div id={`${id}-actions`} className='tw-flex tw-w-full tw-justify-between tw-items-center'>
            <div className='tw-min-w-0'>
                {message && showIndicator ? (
                    <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">
                        {message}
                    </Typography>
                ) : null}
            </div>
            <div className={`tw-flex tw-gap-2 tw-justify-end`}>
                {multiCreate ? (
                    <>
                        <Button id={`${id}-cancel-btn`} data-testid={`${id}-cancel-btn`} variant="secondary" disabled={showIndicator} onClick={guardedClose}>
                            Cancel
                        </Button>
                        {justCreated ? (
                            /* The transient "✓ Created" flash — dead in practice (no live
                               caller passes multiCreate). It stays a raw <button>: a
                               green-filled disabled control has no DS Button equivalent,
                               and re-SHAPING a dead branch buys nothing. Its colour did
                               move onto the success tone (§ 320), which costs nothing and
                               keeps this module's wall at zero without an exemption. */
                            <button
                                id={`${id}-add-more-btn`} data-testid={`${id}-add-more-btn`}
                                disabled
                                className='tw-flex tw-items-center tw-gap-1 tw-bg-tone-success-bold tw-text-ink-inverse tw-rounded tw-px-3 tw-py-1 tw-text-sm tw-font-medium tw-border-0 tw-cursor-default tw-opacity-100'
                            >
                                ✓ {entityName} Created
                            </button>
                        ) : (
                            <Button
                                id={`${id}-add-more-btn`} data-testid={`${id}-add-more-btn`}
                                variant="secondary"
                                disabled={name === ""}
                                loading={showIndicator}
                                onClick={handleCreateAndAddMore}
                            >
                                Create &amp; Add More
                            </Button>
                        )}
                        <Button
                            id={`${id}-submit-btn`} data-testid={`${id}-submit-btn`}
                            variant="primary"
                            disabled={name === ""}
                            loading={showIndicator}
                            onClick={handleCreate}
                        >
                            Create
                        </Button>
                    </>
                ) : (
                    <>
                        <Button id={`${id}-cancel-btn`} data-testid={`${id}-cancel-btn`} variant="secondary" disabled={showIndicator} onClick={guardedClose}>Cancel</Button>
                        <Button data-testid="list-createlistdialog-button-2" id={`${id}-submit-btn`} variant="primary" disabled={name === ""} loading={showIndicator} onClick={handleSingleCreate}>
                            {actionLabel ?? 'Create'}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <Dialog isOpen onClose={guardedClose} title={dialogHeader} data-testid={id} footer={footer}>
            <div id={`${id}-content`}>
                <TextField
                    label={labelName}
                    id={`${id}-name-input`}
                    placeholder={ENTER_NAME}
                    value={name}
                    onChange={(newValue) => setName(newValue)}
                    data-testid="list-createlistdialog-input-1"
                />
            </div>
        </Dialog>
    );
}

export default CreateListDialog;
